import { Divider, Typography, TypographyProps } from '@mui/material'
import { BomItemScaleIcon } from 'features/AnimalScale/BomItemScaleIcon'
import { isEqual } from 'lodash'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { QuantityString } from 'model/Quantity'
import { QuantityDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { bomItemDimensionsSelector } from '../store/selectors/bomItemDimensionsSelector'

type DimensionsProps = {
  title: string
  dimensions: { length: QuantityDto; width: QuantityDto; height: QuantityDto }
  typographyProps?: TypographyProps
}

export const DimensionSummaryString = (dimensions: {
  length: QuantityDto
  width: QuantityDto
  height?: QuantityDto
}) => {
  const orderedDimensions = [
    dimensions.length,
    dimensions.width,
    dimensions.height,
  ].filter((x) => !!x)

  const dimensionsString = orderedDimensions
    .map((dimension, index) => {
      let showUnit = true

      if (
        ['m', 'mm'].includes(dimension.unit) &&
        index < orderedDimensions.length - 1
      ) {
        showUnit = false
      }

      return QuantityString(dimension, {
        showUnit: showUnit,
        showFractionsUsingHTML: true,
        showInchesAsFeetAndInches: true,
      })
    })
    .filter((x) => !!x)
    .join(' x ')

  return dimensionsString
}

const DimensionString = (props: DimensionsProps) => {
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
      <Typography
        variant="caption"
        color="textSecondary"
        {...(props.typographyProps || {})}
      >
        {props.title && <span>{props.title}:</span>}
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        dangerouslySetInnerHTML={{
          __html: DimensionSummaryString(props.dimensions),
        }}
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '4px',
        }}
        {...(props.typographyProps || {})}
      ></Typography>
    </div>
  )
}

export const BomItemDimensionSummary = ({
  bomItemPointer,
  typographyProps,
  rootStyle,
}: {
  bomItemPointer: BomItemPointer
  typographyProps?: TypographyProps
  rootStyle?: React.CSSProperties
}) => {
  const dimensions = useAppSelector(
    bomItemDimensionsSelector(bomItemPointer),
    isEqual,
  )

  if (!dimensions?.weight && !dimensions?.length3D && !dimensions?.length2D) {
    return null
  }

  return (
    <span
      style={{
        display: 'flex',
        gap: '8px',
        marginRight: '8px',
        alignItems: 'flex-end',
        ...rootStyle,
      }}
    >
      <BomItemScaleIcon
        bomItemPointer={bomItemPointer}
        baseStyle={{
          width: '24px',
          height: '24px',
        }}
      />
      {dimensions.weight?.value && (
        <>
          <Typography
            variant="caption"
            color="textSecondary"
            {...typographyProps}
          >
            {QuantityString(dimensions.weight)}
          </Typography>
          <Divider orientation="vertical" flexItem />
        </>
      )}
      {dimensions.length3D && (
        <>
          <DimensionString
            title="3D"
            dimensions={{
              length: dimensions.length3D,
              width: dimensions.width3D,
              height: dimensions.height3D,
            }}
            typographyProps={typographyProps}
          />
          <Divider orientation="vertical" flexItem />
        </>
      )}
      {dimensions.length2D && (
        <>
          <DimensionString
            title="2D"
            dimensions={{
              length: dimensions.length2D,
              width: dimensions.width2D,
              height: undefined,
            }}
            typographyProps={typographyProps}
          />
          {(bomItemPointer.type === BomItemType.assemblyType ||
            bomItemPointer.type === BomItemType.assemblyInstance) && (
            <Divider orientation="vertical" flexItem />
          )}
        </>
      )}
    </span>
  )
}
